import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const fullText = "Building Tomorrow's Web Experiences Today";

  useEffect(() => {
    if (currentIndex < fullText.length && isTyping) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + fullText[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    } else if (currentIndex === fullText.length) {
      setIsTyping(false);
    }
  }, [currentIndex, isTyping, fullText]);

  const particleVariants = {
    animate: {
      y: [0, -20, 0],
      opacity: [0.3, 1, 0.3],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background particle-bg">
      {/* Animated Background Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-brand-cyan rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            variants={particleVariants}
            animate="animate"
            transition={{ delay: i * 0.2 }}
          />
        ))}
      </div>

      {/* Neural Network Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        {/* Main Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="space-y-8"
        >
          {/* Status Indicator */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-morphism cyber-border"
          >
            <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
            <span className="font-mono text-sm text-brand-green">SYSTEM ONLINE</span>
            <span className="font-mono text-xs text-text-secondary">v2.4.7</span>
          </motion.div>

          {/* Main Heading with Typewriter Effect */}
          <div className="space-y-4">
            <h1 className="font-orbitron font-bold text-4xl md:text-6xl lg:text-7xl text-foreground">
              <span className="text-brand-cyan">Cyber</span>Dev{' '}
              <span className="text-brand-purple">Nexus</span>
            </h1>
            
            <div className="h-16 flex items-center justify-center">
              <p className="font-inter text-lg md:text-xl lg:text-2xl text-text-secondary font-medium">
                {displayText}
                {isTyping && (
                  <span className="inline-block w-0.5 h-6 bg-brand-cyan ml-1 animate-pulse"></span>
                )}
              </p>
            </div>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="font-inter text-base md:text-lg text-text-tertiary max-w-2xl mx-auto leading-relaxed"
          >
            Welcome to the digital frontier where cutting-edge technology meets visionary design. 
            Experience the future of web development through immersive interfaces and revolutionary solutions.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8"
          >
            <Link to="/project-dimensions-portfolio">
              <Button
                variant="default"
                size="lg"
                className="bg-gradient-to-r from-brand-cyan to-brand-purple hover:from-brand-purple hover:to-brand-cyan cyber-glow-intense transition-all duration-500 transform hover:scale-105"
                iconName="Zap"
                iconPosition="left"
                iconSize={20}
              >
                Enter the Matrix
              </Button>
            </Link>

            <Link to="/neural-network-contact-collaboration">
              <Button
                variant="outline"
                size="lg"
                className="cyber-border hover:cyber-border-active hover:cyber-glow transition-all duration-300"
                iconName="MessageSquare"
                iconPosition="left"
                iconSize={20}
              >
                Schedule Neural Link
              </Button>
            </Link>
          </motion.div>

          {/* Quick Access Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3 }}
            className="flex items-center justify-center space-x-8 pt-8"
          >
            <Link 
              to="/arsenal-showcase-skills-technologies"
              className="flex items-center space-x-2 text-text-secondary hover:text-brand-cyan transition-colors duration-300 cyber-hover"
            >
              <Icon name="Code" size={16} />
              <span className="font-mono text-sm">Arsenal</span>
            </Link>
            
            <Link 
              to="/neural-network-contact-collaboration"
              className="flex items-center space-x-2 text-text-secondary hover:text-brand-green transition-colors duration-300 cyber-hover"
            >
              <Icon name="MessageSquare" size={16} />
              <span className="font-mono text-sm">Contact</span>
            </Link>
            
            <Link 
              to="/identity-matrix-about"
              className="flex items-center space-x-2 text-text-secondary hover:text-brand-purple transition-colors duration-300 cyber-hover"
            >
              <Icon name="User" size={16} />
              <span className="font-mono text-sm">Identity</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="font-mono text-xs text-text-tertiary">SCROLL TO EXPLORE</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-brand-cyan rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-brand-cyan rounded-full mt-2"
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;